import { useEffect, useRef, useState } from 'react'
import {
  PLANNER_DAY_LONG,
  THRESHOLDS,
  buildRecommendationWhySummary,
  partitionPlannerSuggestions,
} from '../../utils/mealPlanHelpers.js'
import { isNightOffMeal } from '../../utils/nightOff.js'
import { useAuth } from '../../contexts/AuthContext.jsx'
import BottomSheet from '../../design-system/patterns/BottomSheet.jsx'
import Button from '../../design-system/primitives/Button.jsx'
import StateBanner from '../StateBanner/StateBanner.jsx'
import WeekOptionsHeader from './WeekOptionsHeader.jsx'
import NightOffPicker from './NightOffPicker.jsx'
import './planner.css'

const DAY_TITLE = {
  MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday', THU: 'Thursday',
  FRI: 'Friday', SAT: 'Saturday', SUN: 'Sunday',
}

function dayTitleFromKey(dayKey) {
  if (!dayKey) return 'this day'
  return DAY_TITLE[dayKey] || PLANNER_DAY_LONG[dayKey] || dayKey
}

function SwapCandidateRow({
  meal,
  isUserPick,
  isHighlighted = false,
  pickLabel,
  onPick,
  onRemovePick,
  removePickBusy = false,
}) {
  return (
    <div
      className={[
        'swap-recipe-row',
        isHighlighted ? 'swap-recipe-row--plan-highlight' : '',
      ].filter(Boolean).join(' ')}
      data-plan-highlight={isHighlighted ? 'true' : undefined}
    >
      <div className="swap-recipe-row-info">
        <div className="swap-recipe-row-title">
          <span className="planner-meal-name">{meal.name}</span>
          {meal.time && meal.time !== '—' && (
            <span className="meal-row-meta">
              {' · '}
              {meal.time}
            </span>
          )}
        </div>
        <div className="meal-row-sub">
          <span className={`meal-row-source ${isUserPick ? 'match' : meal.source === 'match' ? 'match' : 'added'}`}>
            {isUserPick ? 'Your pick' : meal.source === 'match' ? 'Pantry match' : 'From Recipes'}
          </span>
          <span className="meal-row-meta">
            {' · '}
            {meal.have}
            /
            {meal.need}
            {' in pantry'}
          </span>
        </div>
      </div>
      <div className="swap-recipe-row-actions">
        <Button
          variant="primary"
          className="swap-recipe-pick-btn"
          onClick={() => onPick(meal)}
        >
          {pickLabel}
        </Button>
        {isUserPick && onRemovePick && (
          <button
            type="button"
            className="planner-pick-remove-link"
            disabled={removePickBusy}
            onClick={() => onRemovePick(meal)}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  )
}

function SheetAiHint({ libraryCount, onImportRecipe, density }) {
  const n = typeof libraryCount === 'number' ? libraryCount : null
  const needMore =
    n != null ? Math.max(0, THRESHOLDS.thinLibrary - n) : Math.max(0, THRESHOLDS.thinLibrary - 2)
  const countPhrase =
    needMore === 1 ? 'one more recipe' : `${needMore} more recipes`
  const isMobile = density === 'mobile'

  return (
    <StateBanner
      variant="ai"
      className={isMobile ? 'mobile-planner-ai-hint-banner' : 'planner-ai-hint-banner'}
      body={
        isMobile
          ? <>Need <b>{countPhrase}</b> for a fresher week. Import meals you cook.</>
          : <>Need <b>{countPhrase}</b> for a fresher rotation. Import meals you already cook.</>
      }
      action={
        onImportRecipe
          ? { label: isMobile ? 'Import →' : 'Import recipe →', onClick: onImportRecipe }
          : undefined
      }
    />
  )
}

/**
 * Recipe picker sheet — swap an assigned dinner, or add a dinner to an open day.
 *
 * mode="swap" — requires `meal` (assigned). Header: "Swap {day}'s dinner"
 * mode="add"  — requires `dayKey`. Header: "Add to {day}"
 * An existing easy night uses "Change {day}" and leads with the night choices.
 */
export default function SwapRecipeSheet({
  open,
  mode = 'swap',
  meal = null,
  dayKey: dayKeyProp = null,
  candidates = [],
  plannerState = 'healthy',
  dietary = null,
  libraryCount = null,
  onImportRecipe = null,
  highlightRecipeId = null,
  onRemovePick = null,
  removePickBusy = false,
  density = 'desktop',
  onSaveNightOff = null,
  onClearNight = null,
  onClose,
  onPick,
}) {
  const { user } = useAuth()
  const accountDisplayName =
    (user?.first_name || '').trim() || (user?.email || '').split('@')[0] || 'You'
  const [showWhy, setShowWhy] = useState(false)
  const listRef = useRef(null)
  const nightOffRef = useRef(null)
  const isAdd = mode === 'add'
  const dayKey = isAdd ? dayKeyProp : meal?.assignedTo
  const highlightId = highlightRecipeId != null ? String(highlightRecipeId) : null
  const mealIsHighlighted = (m) =>
    Boolean(highlightId && m?.recipeId && String(m.recipeId) === highlightId)
  const initialNightOffType = isNightOffMeal(meal) ? meal.nightOffType : null
  const [nightOffType, setNightOffType] = useState(initialNightOffType)

  useEffect(() => {
    if (!open) {
      setShowWhy(false)
      return
    }
    setNightOffType(initialNightOffType)
  }, [open, initialNightOffType])

  useEffect(() => {
    if (!open || !highlightId) return undefined
    const timer = window.setTimeout(() => {
      document
        .querySelector('.swap-recipe-sheet [data-plan-highlight="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 120)
    return () => window.clearTimeout(timer)
  }, [open, highlightId, candidates])

  const { userPicks, recommended } = partitionPlannerSuggestions(candidates)
  const hasOptions = userPicks.length + recommended.length > 0
  const blockedState = plannerState === 'cold-start' || plannerState === 'empty-pantry'
  const editingNightOff = !isAdd && isNightOffMeal(meal)
  const showNightOff = Boolean(onSaveNightOff) && (
    editingNightOff || (hasOptions && !blockedState)
  )

  useEffect(() => {
    if (!open || !showNightOff || !initialNightOffType || editingNightOff) return undefined
    const timer = window.setTimeout(() => {
      const list = listRef.current
      const block = nightOffRef.current
      if (!list || !block) return
      const listRect = list.getBoundingClientRect()
      const blockRect = block.getBoundingClientRect()
      if (blockRect.top >= listRect.top && blockRect.bottom <= listRect.bottom) return
      list.scrollTop += (blockRect.bottom - listRect.bottom) + 12
    }, 80)
    return () => window.clearTimeout(timer)
  }, [open, showNightOff, initialNightOffType, editingNightOff, candidates])

  if (!open || (!isAdd && !meal) || (isAdd && !dayKey)) return null

  const dayTitle = dayTitleFromKey(dayKey)
  const title = editingNightOff
    ? `Change ${dayTitle}`
    : isAdd
      ? `Add to ${dayTitle}`
      : `Swap ${dayTitle}'s dinner`
  const pickLabel = isAdd || isNightOffMeal(meal) ? 'Add →' : 'Swap →'
  const emptyCopy = isAdd || editingNightOff
    ? 'No recipes available to add.'
    : 'No other recipes available to swap in.'

  const nightOffBlock = showNightOff ? (
    <div
      className={`swap-recipe-night-off${editingNightOff ? ' is-leading' : ''}`}
      ref={nightOffRef}
    >
      {editingNightOff ? (
        <p className="swap-recipe-night-off-label">This night</p>
      ) : (
        <div className="planner-divider">
          <div className="planner-divider-line" />
          <div className="planner-divider-label">Or take the night off</div>
          <div className="planner-divider-line" />
        </div>
      )}
      <div className="swap-recipe-night-off-card">
        <NightOffPicker
          selected={nightOffType}
          onSelect={setNightOffType}
          onSave={(typeId) => onSaveNightOff?.(dayKey, typeId)}
          saveDisabled={!nightOffType}
        />
        {editingNightOff && onClearNight ? (
          <button
            type="button"
            className="planner-pick-remove-link swap-recipe-clear-night"
            onClick={() => onClearNight()}
          >
            Leave this night open
          </button>
        ) : null}
      </div>
    </div>
  ) : null

  const body = (
    <div className="swap-recipe-sheet">
      <div className="swap-recipe-sheet-header">
        <div className="swap-recipe-sheet-title">{title}</div>
        {density !== 'mobile' && (
          <button
            type="button"
            className="swap-recipe-sheet-close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        )}
      </div>
      <div className="swap-recipe-sheet-list" ref={listRef}>
        {editingNightOff ? nightOffBlock : null}
        <WeekOptionsHeader
          title="Cook something"
          showSubtitle={false}
          userPicksCount={userPicks.length}
          recommendedCount={recommended.length}
          plannerState={plannerState}
        />
        {plannerState === 'thin-library' && (
          <SheetAiHint
            libraryCount={libraryCount}
            onImportRecipe={onImportRecipe}
            density={density}
          />
        )}
        <div className="swap-recipe-rows-card">
        {userPicks.map((m) => (
          <SwapCandidateRow
            key={m.id || m.recipeId}
            meal={m}
            isUserPick
            isHighlighted={mealIsHighlighted(m)}
            pickLabel={pickLabel}
            onPick={onPick}
            onRemovePick={onRemovePick}
            removePickBusy={removePickBusy}
          />
        ))}
        {recommended.map((m) => (
          <SwapCandidateRow
            key={m.id || m.recipeId}
            meal={m}
            isUserPick={false}
            isHighlighted={mealIsHighlighted(m)}
            pickLabel={pickLabel}
            onPick={onPick}
          />
        ))}
        {userPicks.length === 0 && recommended.length === 0 && (
          <div className="swap-recipe-sheet-empty">{emptyCopy}</div>
        )}
        </div>
        {recommended.length > 0 && (
          <div className="swap-recipe-why">
            <button
              type="button"
              className="swap-recipe-why-toggle"
              aria-expanded={showWhy}
              aria-controls="swap-recipe-why-summary"
              onClick={() => setShowWhy((v) => !v)}
            >
              {showWhy ? 'Hide why these were recommended' : 'Why these were recommended'}
            </button>
            <p
              id="swap-recipe-why-summary"
              className="swap-recipe-why-summary"
              hidden={!showWhy}
            >
              {buildRecommendationWhySummary(recommended, {
                dietary,
                accountDisplayName,
              })}
            </p>
          </div>
        )}
        {editingNightOff ? null : nightOffBlock}
      </div>
    </div>
  )

  if (density === 'mobile') {
    return (
      <BottomSheet open={open} onClose={onClose} aria-label={title}>
        {body}
      </BottomSheet>
    )
  }

  return (
    <div
      className="swap-recipe-scrim swap-recipe-scrim--docked"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="swap-recipe-panel"
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {body}
      </div>
    </div>
  )
}
